import { Grid, Typography } from '@mui/material';
import isEqual from 'lodash.isequal';
import React, { startTransition, useEffect, useState } from 'react';
import {
  useGlobalLocaleContext,
  useGlobalTimeSliderContext,
} from '../../../context/contexts';
import { ColoredPaper, StyledButton, StyledSlider } from './component-utils';
import { dateFormatter, displayDateRange } from './utils-display';

export const StartEndPicker = () => {
  const timeSliderHandle = useGlobalTimeSliderContext();
  const localeHandle = useGlobalLocaleContext();
  const locale = localeHandle.selected;
  const [state, dispatch] = timeSliderHandle;
  const [values, setValues] = useState<number[]>([
    state.range.all.findIndex((v) => isEqual(v, state.start)),
    state.range.all.findIndex((v) => isEqual(v, state.end)),
  ]);

  useEffect(() => {
    setValues([
      state.range.all.findIndex((v) => isEqual(v, state.start)),
      state.range.all.findIndex((v) => isEqual(v, state.end)),
    ]);
  }, [state.range, state.end, state.start]);

  const handleLeftLeftClick = () => {
    const oldLeft = values[0];
    const oldRight = values[1];
    const start = state.range.all.at(oldLeft - 1);
    const end = state.range.all.at(oldRight);
    const newLeft = start ? oldLeft - 1 : oldLeft;
    setValues([newLeft, oldRight]);

    startTransition(() => {
      if (start && end && newLeft !== oldLeft) {
        dispatch({ type: 'update-range', start, end });
      }
    });
  };

  const handleLeftRightClick = () => {
    const oldLeft = values[0];
    const oldRight = values[1];
    const start = state.range.all.at(oldLeft + 1);
    const end = state.range.all.at(oldRight);
    const newLeft = start && oldLeft + 1 <= oldRight ? oldLeft + 1 : oldLeft;
    setValues([newLeft, oldRight]);

    startTransition(() => {
      if (start && end && newLeft !== oldLeft) {
        dispatch({ type: 'update-range', start, end });
      }
    });
  };

  const handleRightLeftClick = () => {
    const oldLeft = values[0];
    const oldRight = values[1];
    const start = state.range.all.at(oldLeft);
    const end = state.range.all.at(oldRight - 1);
    const newRight = end && oldRight - 1 >= oldLeft ? oldRight - 1 : oldRight;
    setValues([oldLeft, newRight]);

    startTransition(() => {
      if (start && end && newRight !== oldRight) {
        dispatch({ type: 'update-range', start, end });
      }
    });
  };

  const handleRightRightClick = () => {
    const oldLeft = values[0];
    const oldRight = values[1];
    const start = state.range.all.at(oldLeft);
    const end = state.range.all.at(oldRight + 1);
    const newRight = end ? oldRight + 1 : oldRight;
    setValues([oldLeft, newRight]);

    startTransition(() => {
      if (start && end && newRight !== oldRight) {
        dispatch({ type: 'update-range', start, end });
      }
    });
  };
  return (
    <Grid
      item
      style={{
        margin: 'auto 10px',
        height: '8vh',
        width: '20vw',
      }}
    >
      <ColoredPaper style={{ height: '100%' }}>
        <Grid container justifyContent="center">
          <Grid
            item
            style={{
              flexGrow: 1,
              margin: '1vh 3vw 0 3vw',
              height: '3vh',
            }}
          >
            <Typography
              variant="subtitle1"
              color="secondary"
              style={{
                textAlign: 'center',
              }}
            >
              {displayDateRange(
                state.start,
                state.end,
                state.selectedGranularity,
                locale
              )}
            </Typography>
          </Grid>
          <Grid container item xs={12} spacing={0}>
            <Grid
              item
              container
              xs={2}
              justifyContent="space-around"
              alignItems="center"
            >
              <StyledButton
                onClick={handleLeftLeftClick}
                disabled={values[0] === 0}
                aria-label="move selected left"
              >
                &lt;
              </StyledButton>
              <StyledButton
                onClick={handleLeftRightClick}
                disabled={values[0] === values[1]}
                aria-label="move selected right"
              >
                &gt;
              </StyledButton>
            </Grid>
            <Grid item xs={8} style={{ padding: '0px 4px' }}>
              <StyledSlider
                getAriaLabel={() => 'TimeSlider range'}
                value={values}
                onChange={(e: any, v: number | number[]) => {
                  const arr = v as number[];
                  setValues(arr);
                }}
                onChangeCommitted={(e: any, v: number | number[]) => {
                  startTransition(() => {
                    const [startIndex, endIndex] = v as number[];
                    const start = state.range.all.at(startIndex);
                    const end = state.range.all.at(endIndex);
                    if (start && end) {
                      dispatch({ type: 'update-range', start, end });
                    } else {
                      throw new Error(
                        `index ${startIndex} or ${endIndex} is out of range`
                      );
                    }
                  });
                }}
                valueLabelDisplay="auto"
                min={0}
                max={state.range.all.length - 1}
                getAriaValueText={(value: number) => {
                  const v = state.range.all.at(value);
                  return v
                    ? dateFormatter(v, state.selectedGranularity, locale)
                    : '';
                }}
                valueLabelFormat={(value: number) => {
                  const v = state.range.all.at(value);
                  return v
                    ? dateFormatter(v, state.selectedGranularity, locale)
                    : '';
                }}
              />
            </Grid>
            <Grid
              item
              container
              xs={2}
              justifyContent="space-around"
              alignItems="center"
            >
              <StyledButton
                onClick={handleRightLeftClick}
                disabled={values[0] === values[1]}
                aria-label="move selected left"
              >
                &lt;
              </StyledButton>
              <StyledButton
                onClick={handleRightRightClick}
                disabled={values[1] === state.range.all.length - 1}
                aria-label="move selected right"
              >
                &gt;
              </StyledButton>
            </Grid>
          </Grid>
        </Grid>
      </ColoredPaper>
    </Grid>
  );
};
